import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrganization, useAuth } from '@clerk/clerk-react';
import { useApi } from '../lib/api';

// ============================================================================
// Types
// ============================================================================

interface DocumentationSource {
  id: string;
  organizationId: string;
  name: string;
  sourceType: 'url_crawl' | 'file_upload' | 'manual_text';
  rootUrl?: string;
  selectedPaths?: string[];
  originalFilename?: string;
  fileSizeBytes?: number;
  mimeType?: string;
  syncStatus: 'pending' | 'processing' | 'crawling' | 'ready' | 'error';
  lastSyncedAt?: string;
  lastSyncError?: string;
  pageCount: number;
  chunkCount: number;
  createdAt: string;
}

interface DiscoveredPage {
  url: string;
  path: string;
  title?: string;
}

interface PageGroup {
  path: string;
  count: number;
  pages: DiscoveredPage[];
}

interface SearchResult {
  chunkText: string;
  docTitle: string;
  docUrl: string;
  sectionPath: string;
  similarity: number;
}

// ============================================================================
// Component
// ============================================================================

export function OrgDocumentation() {
  const { organization } = useOrganization();
  const { getToken } = useAuth();
  const { get, post, delete: del } = useApi();

  // Data
  const [sources, setSources] = useState<DocumentationSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modals
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);

  // URL crawl flow
  const [urlInput, setUrlInput] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [discoveredGroups, setDiscoveredGroups] = useState<PageGroup[]>([]);
  const [discoveredPages, setDiscoveredPages] = useState<DiscoveredPage[]>([]);
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const [crawlName, setCrawlName] = useState('');

  // Text modal
  const [textName, setTextName] = useState('');
  const [textContent, setTextContent] = useState('');

  // File modal
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [expandedQueryText, setExpandedQueryText] = useState<string | null>(null);

  // Saving state
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const orgId = organization?.id;

  const fetchSources = useCallback(async () => {
    if (!orgId) return;
    try {
      const data = await get<{ sources: DocumentationSource[] }>(`/organizations/${orgId}/documentation`);
      setSources(data.sources);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sources');
    } finally {
      setLoading(false);
    }
  }, [orgId, get]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  // Poll for crawling sources
  useEffect(() => {
    const crawling = sources.some(s => s.syncStatus === 'crawling' || s.syncStatus === 'processing');
    if (!crawling) return;
    const interval = setInterval(fetchSources, 5000);
    return () => clearInterval(interval);
  }, [sources, fetchSources]);

  // ------ Handlers ------

  async function handleAddText() {
    if (!orgId || !textName.trim() || !textContent.trim()) return;
    setSaving(true);
    setFormError(null);
    try {
      await post(`/organizations/${orgId}/documentation/text`, {
        name: textName.trim(),
        content: textContent.trim(),
      });
      setShowTextModal(false);
      setTextName('');
      setTextContent('');
      fetchSources();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to add text');
    } finally {
      setSaving(false);
    }
  }

  async function handleUploadFile() {
    if (!orgId || !selectedFile) return;
    setSaving(true);
    setFormError(null);
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (fileName.trim()) {
        formData.append('name', fileName.trim());
      }

      const res = await fetch(`/api/organizations/${orgId}/documentation/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }

      setShowFileModal(false);
      setSelectedFile(null);
      setFileName('');
      fetchSources();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setSaving(false);
    }
  }

  async function handleDiscover() {
    if (!orgId || !urlInput.trim()) return;
    setDiscovering(true);
    setFormError(null);
    setDiscoveredGroups([]);
    setDiscoveredPages([]);
    setSelectedUrls(new Set());
    try {
      const data = await post<{ totalPages: number; pages: DiscoveredPage[]; grouped: PageGroup[] }>(
        `/organizations/${orgId}/documentation/discover`,
        { url: urlInput.trim() },
      );
      setDiscoveredPages(data.pages);
      setDiscoveredGroups(data.grouped);
      // Select all by default
      setSelectedUrls(new Set(data.pages.map(p => p.url)));
      setCrawlName(new URL(urlInput.trim()).hostname);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to discover pages');
    } finally {
      setDiscovering(false);
    }
  }

  async function handleStartCrawl() {
    if (!orgId || selectedUrls.size === 0) return;
    setSaving(true);
    setFormError(null);
    try {
      await post(`/organizations/${orgId}/documentation/crawl`, {
        name: crawlName.trim() || new URL(urlInput).hostname,
        rootUrl: urlInput.trim(),
        selectedUrls: Array.from(selectedUrls),
      });
      setShowUrlModal(false);
      setUrlInput('');
      setDiscoveredGroups([]);
      setDiscoveredPages([]);
      setSelectedUrls(new Set());
      setCrawlName('');
      fetchSources();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to start crawl');
    } finally {
      setSaving(false);
    }
  }

  async function handleSync(sourceId: string) {
    if (!orgId) return;
    try {
      await post(`/organizations/${orgId}/documentation/${sourceId}/sync`);
      fetchSources();
    } catch (err) {
      console.error('Sync failed:', err);
    }
  }

  async function handleDelete(sourceId: string) {
    if (!orgId) return;
    try {
      await del(`/organizations/${orgId}/documentation/${sourceId}`);
      setDeleteTarget(null);
      fetchSources();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  }

  async function handleSearch() {
    if (!orgId || !searchQuery.trim()) return;
    setSearching(true);
    setExpandedQueryText(null);
    try {
      const data = await post<{ results: SearchResult[]; expandedQuery?: string }>(
        `/organizations/${orgId}/documentation/search`,
        { query: searchQuery.trim(), topK: 5 },
      );
      setSearchResults(data.results);
      setExpandedQueryText(data.expandedQuery || null);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  }

  function toggleGroupUrls(group: PageGroup) {
    const newSelected = new Set(selectedUrls);
    const allSelected = group.pages.every(p => newSelected.has(p.url));
    for (const page of group.pages) {
      if (allSelected) {
        newSelected.delete(page.url);
      } else {
        newSelected.add(page.url);
      }
    }
    setSelectedUrls(newSelected);
  }

  // ------ Render ------

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
            <Link to="/settings" className="hover:text-white transition-colors">Settings</Link>
            <span>/</span>
            <span className="text-slate-300">Knowledge Base</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Knowledge Base</h1>
          <p className="mt-1 text-slate-400">
            Add your product docs, playbooks, and reference materials. This powers fact-checking and live document lookup during calls.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {sources.length > 0 && (
            <button
              onClick={() => setShowSearchModal(true)}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-md transition-colors"
            >
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Test Search
              </span>
            </button>
          )}
          <div className="relative">
            <button
              onClick={() => setShowAddMenu(!showAddMenu)}
              className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
            >
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Source
              </span>
            </button>

            {showAddMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowAddMenu(false)} />
                <div className="absolute right-0 mt-2 w-56 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-20 overflow-hidden">
                  <button
                    onClick={() => { setShowUrlModal(true); setShowAddMenu(false); setFormError(null); }}
                    className="w-full px-4 py-3 text-left hover:bg-slate-700 transition-colors flex items-center gap-3"
                  >
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-white">Crawl URL</p>
                      <p className="text-xs text-slate-400">Import from a docs site</p>
                    </div>
                  </button>
                  <button
                    onClick={() => { setShowFileModal(true); setShowAddMenu(false); setFormError(null); }}
                    className="w-full px-4 py-3 text-left hover:bg-slate-700 transition-colors flex items-center gap-3 border-t border-slate-700"
                  >
                    <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-white">Upload File</p>
                      <p className="text-xs text-slate-400">PDF, DOCX, TXT, Markdown</p>
                    </div>
                  </button>
                  <button
                    onClick={() => { setShowTextModal(true); setShowAddMenu(false); setFormError(null); }}
                    className="w-full px-4 py-3 text-left hover:bg-slate-700 transition-colors flex items-center gap-3 border-t border-slate-700"
                  >
                    <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-white">Paste Text</p>
                      <p className="text-xs text-slate-400">Paste content directly</p>
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Sources List */}
      {sources.length === 0 ? (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-12 text-center">
          <svg className="w-12 h-12 text-slate-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className="text-lg font-medium text-slate-300 mb-1">No documentation yet</h3>
          <p className="text-sm text-slate-500 mb-4">
            Add your product docs, playbooks, or knowledge base articles to enable fact-checking and document lookup during calls.
          </p>
          <button
            onClick={() => setShowAddMenu(true)}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
          >
            Add Your First Source
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {sources.map(source => (
            <div
              key={source.id}
              className="bg-slate-800 rounded-lg border border-slate-700 p-4 hover:border-slate-600 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <SourceIcon type={source.sourceType} />
                  <div className="min-w-0">
                    <h3 className="font-medium text-white truncate">{source.name}</h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <SourceTypeBadge type={source.sourceType} />
                      <StatusBadge status={source.syncStatus} />
                      {source.chunkCount > 0 && (
                        <span className="text-xs text-slate-500">
                          {source.chunkCount} chunks
                          {source.pageCount > 0 && ` from ${source.pageCount} pages`}
                        </span>
                      )}
                      {source.originalFilename && (
                        <span className="text-xs text-slate-500">{source.originalFilename}</span>
                      )}
                      {source.rootUrl && (
                        <span className="text-xs text-slate-500 truncate max-w-xs">{source.rootUrl}</span>
                      )}
                    </div>
                    {source.lastSyncError && (
                      <p className="text-xs text-red-400 mt-1">{source.lastSyncError}</p>
                    )}
                    {source.lastSyncedAt && (
                      <p className="text-xs text-slate-600 mt-1">
                        Last synced {new Date(source.lastSyncedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0 ml-4">
                  {source.sourceType === 'url_crawl' && source.syncStatus === 'ready' && (
                    <button
                      onClick={() => handleSync(source.id)}
                      className="p-1.5 text-slate-400 hover:text-white transition-colors rounded"
                      title="Re-sync"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={() => setDeleteTarget(source.id)}
                    className="p-1.5 text-slate-400 hover:text-red-400 transition-colors rounded"
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ================================================================ */}
      {/* URL Crawl Modal */}
      {/* ================================================================ */}
      {showUrlModal && (
        <Modal title="Crawl Documentation URL" onClose={() => { setShowUrlModal(false); setDiscoveredGroups([]); setDiscoveredPages([]); setFormError(null); }}>
          {discoveredPages.length === 0 ? (
            /* Step 1: Enter URL */
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-300 mb-1">Documentation URL</label>
                <input
                  type="url"
                  value={urlInput}
                  onChange={e => setUrlInput(e.target.value)}
                  placeholder="https://docs.example.com"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-500 focus:border-green-500 focus:outline-none"
                />
                <p className="text-xs text-slate-500 mt-1">We'll discover pages via sitemap or by crawling links.</p>
              </div>
              {formError && <p className="text-sm text-red-400">{formError}</p>}
              <div className="flex justify-end gap-2">
                <button onClick={() => { setShowUrlModal(false); setFormError(null); }} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancel</button>
                <button
                  onClick={handleDiscover}
                  disabled={discovering || !urlInput.trim()}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium rounded-md"
                >
                  {discovering ? 'Discovering...' : 'Discover Pages'}
                </button>
              </div>
            </div>
          ) : (
            /* Step 2: Select pages */
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-300 mb-1">Source Name</label>
                <input
                  value={crawlName}
                  onChange={e => setCrawlName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-300">
                    Found {discoveredPages.length} pages
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedUrls(new Set(discoveredPages.map(p => p.url)))}
                      className="text-xs text-green-400 hover:text-green-300"
                    >
                      Select all
                    </button>
                    <button
                      onClick={() => setSelectedUrls(new Set())}
                      className="text-xs text-slate-400 hover:text-white"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="max-h-64 overflow-y-auto border border-slate-600 rounded-md">
                  {discoveredGroups.map(group => {
                    const allSelected = group.pages.every(p => selectedUrls.has(p.url));
                    const someSelected = group.pages.some(p => selectedUrls.has(p.url));
                    return (
                      <div key={group.path} className="border-b border-slate-700 last:border-b-0">
                        <button
                          onClick={() => toggleGroupUrls(group)}
                          className="w-full px-3 py-2 flex items-center gap-2 hover:bg-slate-700/50 text-left"
                        >
                          <span className={`w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 ${
                            allSelected ? 'bg-green-600 border-green-600' :
                            someSelected ? 'bg-green-600/30 border-green-600' :
                            'border-slate-500'
                          }`}>
                            {(allSelected || someSelected) && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </span>
                          <span className="text-sm font-medium text-white">{group.path}</span>
                          <span className="text-xs text-slate-500 ml-auto">{group.count} pages</span>
                        </button>
                      </div>
                    );
                  })}
                </div>

                <p className="text-xs text-slate-500 mt-1">
                  {selectedUrls.size} of {discoveredPages.length} pages selected (max 500)
                </p>
              </div>

              {formError && <p className="text-sm text-red-400">{formError}</p>}

              <div className="flex justify-between">
                <button
                  onClick={() => { setDiscoveredGroups([]); setDiscoveredPages([]); setSelectedUrls(new Set()); }}
                  className="px-4 py-2 text-sm text-slate-400 hover:text-white"
                >
                  Back
                </button>
                <div className="flex gap-2">
                  <button onClick={() => { setShowUrlModal(false); setFormError(null); }} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancel</button>
                  <button
                    onClick={handleStartCrawl}
                    disabled={saving || selectedUrls.size === 0}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium rounded-md"
                  >
                    {saving ? 'Starting...' : `Crawl ${selectedUrls.size} Pages`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* ================================================================ */}
      {/* File Upload Modal */}
      {/* ================================================================ */}
      {showFileModal && (
        <Modal title="Upload Document" onClose={() => { setShowFileModal(false); setFormError(null); }}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">File</label>
              <div className="relative">
                <input
                  type="file"
                  accept=".pdf,.docx,.doc,.txt,.md"
                  onChange={e => {
                    const file = e.target.files?.[0] || null;
                    setSelectedFile(file);
                    if (file && !fileName) setFileName(file.name.replace(/\.[^.]+$/, ''));
                  }}
                  className="w-full text-sm text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-slate-700 file:text-white hover:file:bg-slate-600 file:cursor-pointer"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">PDF, DOCX, TXT, Markdown. Max 25MB.</p>
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Name (optional)</label>
              <input
                value={fileName}
                onChange={e => setFileName(e.target.value)}
                placeholder="Document name"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-500 focus:border-green-500 focus:outline-none"
              />
            </div>
            {formError && <p className="text-sm text-red-400">{formError}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowFileModal(false); setFormError(null); }} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancel</button>
              <button
                onClick={handleUploadFile}
                disabled={saving || !selectedFile}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium rounded-md"
              >
                {saving ? 'Uploading...' : 'Upload & Process'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ================================================================ */}
      {/* Paste Text Modal */}
      {/* ================================================================ */}
      {showTextModal && (
        <Modal title="Add Text Content" onClose={() => { setShowTextModal(false); setFormError(null); }}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Name</label>
              <input
                value={textName}
                onChange={e => setTextName(e.target.value)}
                placeholder="e.g. Product FAQ, Sales Playbook"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-500 focus:border-green-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Content</label>
              <textarea
                value={textContent}
                onChange={e => setTextContent(e.target.value)}
                placeholder="Paste your text content here..."
                rows={10}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-500 focus:border-green-500 focus:outline-none resize-y"
              />
              <p className="text-xs text-slate-500 mt-1">
                Markdown formatting is preserved. Use headings to improve search quality.
              </p>
            </div>
            {formError && <p className="text-sm text-red-400">{formError}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowTextModal(false); setFormError(null); }} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancel</button>
              <button
                onClick={handleAddText}
                disabled={saving || !textName.trim() || !textContent.trim()}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium rounded-md"
              >
                {saving ? 'Processing...' : 'Add & Process'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ================================================================ */}
      {/* Search Modal */}
      {/* ================================================================ */}
      {showSearchModal && (
        <Modal title="Test Knowledge Base Search" onClose={() => { setShowSearchModal(false); setSearchResults([]); setSearchQuery(''); setExpandedQueryText(null); }}>
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Ask a question about your docs..."
                className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-500 focus:border-green-500 focus:outline-none"
              />
              <button
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim()}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium rounded-md"
              >
                {searching ? '...' : 'Search'}
              </button>
            </div>

            {expandedQueryText && (
              <p className="text-xs text-slate-500">
                Expanded: <span className="text-slate-400">{expandedQueryText}</span>
              </p>
            )}

            {searchResults.length > 0 && (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {searchResults.map((r, i) => (
                  <div key={i} className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {r.docTitle && <span className="text-xs font-medium text-green-400">{r.docTitle}</span>}
                        {r.sectionPath && <span className="text-xs text-slate-500">{r.sectionPath}</span>}
                      </div>
                      <span className="text-xs text-slate-500">{(r.similarity * 100).toFixed(0)}% match</span>
                    </div>
                    <p className="text-sm text-slate-300 whitespace-pre-wrap line-clamp-4">{r.chunkText}</p>
                    {r.docUrl && (
                      <a href={r.docUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 mt-1 inline-block">
                        {r.docUrl}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {searchResults.length === 0 && searchQuery && !searching && (
              <p className="text-sm text-slate-500 text-center py-4">No results found. Try a different query.</p>
            )}
          </div>
        </Modal>
      )}

      {/* ================================================================ */}
      {/* Delete Confirmation */}
      {/* ================================================================ */}
      {deleteTarget && (
        <Modal title="Delete Documentation Source" onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-slate-300 mb-4">
            This will permanently delete this source and all its indexed content. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancel</button>
            <button
              onClick={() => handleDelete(deleteTarget)}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md"
            >
              Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 className="text-lg font-medium text-white">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-6 py-4">
          {children}
        </div>
      </div>
    </div>
  );
}

function SourceIcon({ type }: { type: string }) {
  const classes = "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5";
  switch (type) {
    case 'url_crawl':
      return (
        <div className={`${classes} bg-blue-900/40`}>
          <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </div>
      );
    case 'file_upload':
      return (
        <div className={`${classes} bg-emerald-900/40`}>
          <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
      );
    default:
      return (
        <div className={`${classes} bg-purple-900/40`}>
          <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
      );
  }
}

function SourceTypeBadge({ type }: { type: string }) {
  const labels: Record<string, string> = {
    url_crawl: 'URL Crawl',
    file_upload: 'File Upload',
    manual_text: 'Text',
  };
  const colors: Record<string, string> = {
    url_crawl: 'bg-blue-900/50 text-blue-400',
    file_upload: 'bg-emerald-900/50 text-emerald-400',
    manual_text: 'bg-purple-900/50 text-purple-400',
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded ${colors[type] || 'bg-slate-600 text-slate-300'}`}>
      {labels[type] || type}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; color: string }> = {
    ready: { label: 'Ready', color: 'bg-green-900/50 text-green-400' },
    processing: { label: 'Processing...', color: 'bg-yellow-900/50 text-yellow-400' },
    crawling: { label: 'Crawling...', color: 'bg-yellow-900/50 text-yellow-400' },
    pending: { label: 'Pending', color: 'bg-slate-600 text-slate-300' },
    error: { label: 'Error', color: 'bg-red-900/50 text-red-400' },
  };
  const c = config[status] || { label: status, color: 'bg-slate-600 text-slate-300' };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.color}`}>
      {(status === 'processing' || status === 'crawling') && (
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-current animate-pulse mr-1" />
      )}
      {c.label}
    </span>
  );
}
